"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { Button } from "@/components/ui/button"
import { CheckCircle, Flame, Calendar, ArrowRight, Zap, Target, Clock, CheckCircle2 } from "lucide-react"
import { AnimatedCounter } from "./animated-counter"
import { useSound } from "@/hooks/use-sound"
import { triggerHapticFeedback } from "@/lib/haptics"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { useGymCheckins } from "@/hooks/useGymCheckins"
import { useExercises } from "@/context/ExerciseContext"
import { statsApi } from '@/services/api'
import type { WorkoutStats } from '@/types'
import CommitmentButton from "./CommitmentButton"
import { usePostHog } from 'posthog-js/react'

interface HeroExperienceProps {
  soundEnabled: boolean
  showFullHero?: boolean
  onCheckInComplete?: () => void
}

export default function HeroExperience({ soundEnabled, showFullHero = true, onCheckInComplete }: HeroExperienceProps) {
  const [checkedInTime, setCheckedInTime] = useState<string | null>(null)
  const [stats, setStats] = useState<WorkoutStats | null>(null)
  const playCheckInSound = useSound("https://blob.v0.dev/sounds/chime.mp3", { soundEnabled, volume: 0.6 })
  const navigate = useNavigate()
  const posthog = usePostHog()

  // Use real API data instead of mock state
  const {
    createCheckin,
    isCheckedInToday,
    getLatestCheckinInfo,
    loading,
    error,
    getMonthlyCheckins,
    isCreatingCheckin
  } = useGymCheckins()

  // Get weekly streak from stats API (same as ProgressDashboard)
  useEffect(() => {
    const fetchStats = async () => {
      const result = await statsApi.getStats();
      if (result.success) {
        setStats(result.data || null);
      }
    };
    fetchStats();
  }, []);

  // Get today's exercise data
  const { getTodayExercises } = useExercises()

  const isCheckedIn = isCheckedInToday()
  const currentStreak = stats?.gymStreak || 0 // Use weekly streak from backend
  const monthlyVisits = getMonthlyCheckins()
  const todayExercises = isCheckedIn ? getTodayExercises() : []
  const hasLoggedExercises = todayExercises.length > 0

  const handleCheckIn = async () => {
    // Smart check-in: if already checked in, navigate to workout page
    if (isCheckedIn) {
      playCheckInSound()
      triggerHapticFeedback("light")
      navigate('/workout')
      return
    }

    // Normal check-in flow for new check-ins
    await createCheckin(() => {
      // This callback runs AFTER React Query cache is updated
      playCheckInSound()
      triggerHapticFeedback("success")
      setCheckedInTime(new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }))

      // Track check-in event in PostHog
      posthog.capture('gym_checkin')

      onCheckInComplete?.()

      // Navigate after successful check-in if no onCheckInComplete callback
      if (!onCheckInComplete) {
        navigate('/workout')
      }
    })
  }

  const getTodayDate = () => {
    return new Date().toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })
  }

  const TodoItem = ({
    children,
    completed,
    current,
  }: {
    children: React.ReactNode
    completed: boolean
    current?: boolean
  }) => (
    <motion.div
      className={cn(
        "flex items-center justify-between py-3 px-1 transition-all duration-200",
        current && "text-foreground font-medium",
      )}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <span
        className={cn(
          "text-sm",
          completed ? "text-muted-foreground line-through" : current ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {children}
      </span>
      <div
        className={cn(
          "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200",
          completed
            ? "bg-green-500 border-green-500"
            : current
              ? "border-primary bg-primary/10"
              : "border-muted-foreground/30",
        )}
      >
        {completed && <CheckCircle2 className="w-3 h-3 text-white" />}
      </div>
    </motion.div>
  )

  const renderContent = () => {
    if (isCheckedIn && !showFullHero) {
      return (
        <motion.div
          key="checked-in"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3 }}
          className="bg-card border border-border rounded-xl p-6 transition-shadow hover:shadow-lg"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-bold tracking-tight">Checked in for today</h2>
                <p className="text-muted-foreground">
                  {checkedInTime ? `at ${checkedInTime} — ` : ""}Ready to log your workout.
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-8">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-2 mb-1">
                        <Flame className="w-5 h-5 text-orange-500" />
                        <span className="text-3xl font-bold tracking-tight">
                          <AnimatedCounter value={currentStreak} />
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground font-medium">week streak</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Consecutive weeks with 3+ gym visits.</p>
                    {stats && (
                      <p className="text-xs opacity-75">
                        {stats.currentWeekVisits} visits this week, {stats.daysLeftInWeek} days left
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-center">
                      <div className="flex items-center justify-center space-x-2 mb-1">
                        <Calendar className="w-5 h-5 text-blue-500" />
                        <span className="text-3xl font-bold tracking-tight">
                          <AnimatedCounter value={monthlyVisits} />
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground font-medium">this month</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Total gym visits this month.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </motion.div>
      )
    }

    // Hero for users who are not yet checked in
    return (
      <motion.div
        key="hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="py-8 md:py-12"
      >
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          {/* Left Column - Main Content */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-none">
                Habit building
                <br />
                <span className="text-muted-foreground">without the noise</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-lg">
                <strong className="text-foreground font-semibold">Simple, focused, and blazingly effective.</strong>{" "}
                Altitude is designed with habit formation in mind. It's the first fitness tracker your future self will
                actually thank you for using.
              </p>
            </div>

            {/* Feature Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
              <motion.div
                className="flex items-center space-x-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                <Zap className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm font-medium">Designed for consistency</span>
              </motion.div>
              <motion.div
                className="flex items-center space-x-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
              >
                <Target className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm font-medium">Habit-first approach</span>
              </motion.div>
              <motion.div
                className="flex items-center space-x-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 }}
              >
                <Clock className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm font-medium">Simple and clean</span>
              </motion.div>
              <motion.div
                className="flex items-center space-x-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.6 }}
              >
                <CheckCircle className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm font-medium">Progress focused</span>
              </motion.div>
            </div>

            {/* Daily Commitment - ADHD-friendly intention setting */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.6 }}
            >
              <CommitmentButton />
            </motion.div>

            <motion.div
              whileTap={{ scale: 0.97 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.7 }}
            >
              <Button
                onClick={handleCheckIn}
                disabled={loading || isCreatingCheckin}
                size="lg"
                className="h-12 px-8 text-base font-medium group"
              >
                {loading
                  ? 'Loading...'
                  : isCreatingCheckin
                    ? 'Checking in...'
                    : isCheckedIn
                      ? 'Start workout'
                      : 'Check in to gym'
                }
                {!loading && !isCreatingCheckin && <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-0.5" />}
              </Button>
            </motion.div>

            {/* Error Display with Grace Period Info */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-destructive/10 border border-destructive/20 p-4 rounded-lg"
              >
                <div className="text-destructive text-sm font-medium mb-2">{error}</div>
                {(() => {
                  const latestInfo = getLatestCheckinInfo();
                  if (latestInfo && latestInfo.isWithinGracePeriod) {
                    return (
                      <div className="text-xs text-muted-foreground">
                        Last check-in: {new Date(latestInfo.timestamp).toLocaleString()}
                        <br />
                        Next check-in available: {new Date(latestInfo.nextAllowedTime).toLocaleString()}
                      </div>
                    );
                  }
                  return null;
                })()}
              </motion.div>
            )}
          </motion.div>

          {/* Right Column - Today's To-Do Card */}
          <motion.div
            className="lg:pl-8"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-gradient-to-br from-card via-card to-accent/20 border border-border/50 rounded-2xl p-6 shadow-xl shadow-black/5 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Today</h3>
                <span className="text-sm text-muted-foreground font-medium">{getTodayDate()}</span>
              </div>

              <div className="space-y-1">
                <TodoItem completed={isCheckedIn} current={!isCheckedIn}>
                  Check in to gym
                </TodoItem>
                <TodoItem completed={hasLoggedExercises} current={false}>
                  Log first exercise
                </TodoItem>
                {hasLoggedExercises ? (
                  <TodoItem completed={false} current={false}>
                    Return tomorrow
                  </TodoItem>
                ) : (
                  <TodoItem completed={false} current={false}>
                    Complete workout
                  </TodoItem>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div layout transition={{ duration: 0.5, type: "spring", bounce: 0.2 }}>
      <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
    </motion.div>
  )
}